import React, { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { useData } from "@/context/DataContext";
import { buildArchivo, porFechaDesc, docIdsDe } from "@/lib/archivo";
import { CompactMatterRow } from "@/components/MatterCard";
import { fechaLarga } from "@/lib/fechas";
import { DocLink } from "@/components/DocLink";

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const etiqueta = (items, count) => {
  if (count > 0 || !items?.length) return plural(count, "documento", "documentos");
  const asuntos = items.filter((it) => it.kind === "matter").length;
  return asuntos ? `${plural(asuntos, "asunto", "asuntos")} · sin documento` : plural(0, "documento", "documentos");
};

export const Carpeta = ({ icon, label, count, items, onClick, testid, idx = 0, tono = "#3A6B56", lead, vacio }) => {
  const I = Icons[icon] || Icons.Folder;
  return (
    <button data-testid={testid} onClick={onClick} style={{ animationDelay: `${idx * 40}ms` }}
      className="aldia-fade-up w-full flex items-center gap-4 bg-white border border-[#E5DFD5] rounded-2xl p-4 text-left hover:border-[#3A6B56] hover:shadow-sm transition-[border-color,box-shadow]">
      {lead || (
        <div className="relative w-12 h-12 shrink-0">
          <Icons.Folder size={48} className="absolute inset-0" style={{ color: tono, opacity: 0.18 }} fill="currentColor" strokeWidth={0} />
          <I size={22} className="absolute left-[13px] top-[15px]" style={{ color: tono }} />
        </div>
      )}
      <span className="flex-1 min-w-0 font-heading text-lg font-bold text-[#1C2421] leading-tight">{label}</span>
      <span data-testid={`${testid}-count`} className={`text-sm font-bold rounded-full px-3 py-1 shrink-0 ${vacio ? "text-[#6E7A74] bg-[#F2EDE4]" : "text-[#2E5645] bg-[#E8F0EC]"}`}>
        {vacio || etiqueta(items, count)}
      </span>
      <Icons.ChevronRight size={20} className="text-[#9AA39D] shrink-0" />
    </button>
  );
};

const Item = ({ it, idx }) => {
  if (it.kind === "matter") return <CompactMatterRow matter={it.matter} index={idx} />;
  return (
    <div data-testid={`archivo-item-${it.key}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-3 space-y-2">
      <div>
        <p className="font-semibold text-base text-[#1C2421]">{it.title}</p>
        <p className="text-sm text-[#6E7A74]">{[it.subtitle, it.date && fechaLarga(it.date.slice(0, 10))].filter(Boolean).join(" · ")}</p>
      </div>
      {it.doc && <DocLink doc={it.doc} testid={`archivo-doc-${it.key}`} />}
    </div>
  );
};

export const Migas = ({ partes, onGo }) => (
  <nav className="flex flex-wrap items-center gap-1 text-sm" data-testid="archivo-migas">
    {partes.map((p, i) => (
      <React.Fragment key={i}>
        {i > 0 && <Icons.ChevronRight size={14} className="text-[#9AA39D]" />}
        {i < partes.length - 1
          ? <button onClick={() => onGo(p.params)} className="font-semibold text-[#3A6B56] hover:underline" data-testid={`archivo-miga-${i}`}>{p.label}</button>
          : <span className="font-semibold text-[#1C2421]">{p.label}</span>}
      </React.Fragment>
    ))}
  </nav>
);

export default function Archivo() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const data = useData();
  const areas = useMemo(() => buildArchivo(data), [data]);
  const a = areas.find((x) => x.id === sp.get("a"));
  const e = a?.entidades?.find((x) => x.id === sp.get("e"));
  const unaSeccion = e && e.seccionesDef.length === 1;
  const sId = unaSeccion ? e.seccionesDef[0].id : sp.get("s");
  const s = e?.seccionesDef.find((x) => x.id === sId);
  const go = (params) => setSp(params);

  const partes = [{ label: "Archivo", params: {} }];
  if (a) partes.push({ label: a.label, params: { a: a.id } });
  if (e) partes.push({ label: e.label, params: { a: a.id, e: e.id } });
  if (s && !unaSeccion) partes.push({ label: s.label, params: { a: a.id, e: e.id, s: s.id } });
  const atras = () => (partes.length > 1 ? go(partes[partes.length - (s && !unaSeccion ? 2 : 2)].params) : navigate(-1));

  let contenido;
  if (!a) {
    contenido = areas.length === 0
      ? <p className="text-base text-[#6E7A74] bg-white border border-dashed border-[#E5DFD5] rounded-2xl p-5 text-center" data-testid="archivo-vacio">Aún no hay documentación al día.</p>
      : areas.map((x, i) => <Carpeta key={x.id} idx={i} icon={x.icon} label={x.label} count={x.count} items={x.id === "pendientes" ? x.items : x.entidades.flatMap((en) => Object.values(en.items).flat())} testid={`archivo-area-${x.id}`}
          tono={x.id === "pendientes" ? "#D97706" : "#3A6B56"} onClick={() => go({ a: x.id })} />);
  } else if (a.id === "pendientes") {
    contenido = porFechaDesc(a.items).map((it, i) => <Item key={it.key} it={it} idx={i} />);
  } else if (!e) {
    contenido = a.entidades.map((x, i) => <Carpeta key={x.id} idx={i} icon={a.icon} label={x.label} count={x.count} items={Object.values(x.items).flat()} testid={`archivo-entidad-${x.id}`} onClick={() => go({ a: a.id, e: x.id })} />);
  } else if (!s) {
    contenido = e.seccionesDef.filter((x) => e.items[x.id].length > 0).map((x, i) => (
      <Carpeta key={x.id} idx={i} icon={x.icon} label={x.label} count={docIdsDe(e.items[x.id]).size}
        items={e.items[x.id]} testid={`archivo-seccion-${x.id}`} onClick={() => go({ a: a.id, e: e.id, s: x.id })} />
    ));
  } else {
    contenido = porFechaDesc(e.items[s.id]).map((it, i) => <Item key={it.key} it={it} idx={i} />);
  }

  return (
    <div className="space-y-5" data-testid="archivo-page">
      <button data-testid="archivo-back" onClick={atras} className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]">
        <Icons.ChevronLeft size={22} /> Volver
      </button>
      <header className="aldia-fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421] flex items-center gap-2">
          <Icons.Archive size={28} className="text-[#16A34A]" /> Documentación al día
        </h1>
        <p className="text-base text-[#6E7A74] mt-1">Tu archivo organizado por temas. {s || a?.id === "pendientes" ? "Los más recientes primero." : "Toca una carpeta para abrirla."}</p>
      </header>
      {a && <Migas partes={partes} onGo={go} />}
      <div className="space-y-3" data-testid="archivo-contenido">{contenido}</div>
    </div>
  );
}
